/**
 * 天气主题总表 —— 全站背景渐变、文字明暗、前景元素层都由这里驱动。
 *
 * 一份数据源解决三件事，避免配色/图层/文字色分散在多处互相打架：
 *
 *   gradient  页面背景渐变（自上而下，第一个颜色在顶部）
 *   textMode  'light' = 深色底 → 浅色文字；'dark' = 浅色底 → 深色文字
 *   veil      叠在渐变上的薄纱，用来压出对比度（深底压暗顶部、浅底提亮顶部）
 *   layers    前景元素层（太阳/月亮/云/雨丝/雪幕/雾团…）
 *
 * 元素层的两种摆放方式：
 *
 *   full()  整块铺满 hero（雨丝、雪幕、星空这类「全屏纹理」）。切图自带
 *           alpha 渐变边缘，直接 inset:0 + object-fit:cover 即可。
 *
 *   spot()  单个主体元素（太阳、月亮、云团、雾团…）。切图内部有大片透明
 *           留白，按原图盒子摆会把主体带偏，所以按 CENTROID 里统计出的
 *           「可见内容质心」把主体锚定到目标点：先 left/top 放到目标点，
 *           再用 translate(-cx%, -cy%) 把质心拉过去。换一套画风（留白比例
 *           不同）也不用重调坐标。
 *
 * 切图放在 `public/weather-overlay/<theme>/*.webp`（由 4096px 原稿降采样到
 * 1280px + WebP 压缩，整包 ~2.8MB，单个主题最重 ~360KB）。
 */
const BASE = '/weather-overlay'
const src = (theme, name) => `${BASE}/${theme}/${name}.webp`

/**
 * 每张切图「可见内容」的 alpha 质心（占自身图片的百分比）。
 * 由 alpha 通道加权统计得出；未列出的按正中间处理。
 */
const CENTROID = {
  'sunny/sun': [49.9, 49.7],
  'sunny/halo': [49.7, 49.6],
  'sunny/rays': [38.4, 36.6],
  'sunny/flare': [65.9, 65.9],
  'clear-night/moon': [49.8, 49.8],
  'clear-night/nebula': [50.7, 45.1],
  'cloudy/cloud': [50.5, 49.3],
  'cloudy/dim75': [52.8, 34.5],
  'cloudy/dim77': [42.8, 48.1],
  'overcast/dim75': [52.8, 34.5],
  'overcast/dim77': [42.8, 48.1],
  'rain/dim77': [42.8, 48.1],
  'snow/dim75': [52.8, 34.5],
  'snow/dim77': [42.8, 48.1],
  'hail/dim77': [42.8, 48.1],
  'sleet/extra96': [53.2, 36.8],
  'haze/cloud': [39.5, 52.8],
  'haze/fog': [49.2, 49.9],
  'fog/cloud': [39.5, 52.8],
  'fog/fog': [49.2, 49.9],
  'hail/hkl': [58.2, 47.2],
  'hail/water': [54.4, 51.1],
  'dust/sandstrip': [50.7, 45.1]
}
const centroidOf = (theme, name) => CENTROID[`${theme}/${name}`] || [50, 50]

/** 全屏纹理层：铺满 hero，靠切图自身的 alpha 渐变收边。 */
function full(theme, name, { opacity = 1, blend = 'normal' } = {}) {
  return {
    src: src(theme, name),
    style: { inset: '0', opacity, mixBlendMode: blend, objectFit: 'cover' }
  }
}

/**
 * 主体元素层：把「可见内容质心」锚定到 hero 的 (x%, y%)。
 * @param {number} w  元素宽度，占 hero 宽度的百分比
 * @param {[number,number]} at  质心在 hero 中的落点（百分比）
 */
function spot(theme, name, { w, at, opacity = 1, blend = 'normal' } = {}) {
  const [cx, cy] = centroidOf(theme, name)
  return {
    src: src(theme, name),
    style: {
      left: `${at[0]}%`,
      top: `${at[1]}%`,
      width: `${w}%`,
      transform: `translate(${-cx}%, ${-cy}%)`,
      opacity,
      mixBlendMode: blend
    }
  }
}

/* ---------- 元素层组合 ---------- */

/* 晴天：太阳 + 光晕 + 光线 + 镜头色散，全部同心锚在同一点上 */
const SUN_AT = [71, 30]
const SUNNY = [
  spot('sunny', 'rays', { w: 155, at: SUN_AT, opacity: 0.42, blend: 'screen' }),
  spot('sunny', 'flare', { w: 66, at: [63, 41], opacity: 0.44, blend: 'screen' }),
  spot('sunny', 'halo', { w: 88, at: SUN_AT, opacity: 0.62, blend: 'screen' }),
  spot('sunny', 'sun', { w: 44, at: SUN_AT, opacity: 0.95, blend: 'screen' })
]

/* 晴夜：星云氛围 + 星点 + 月亮 */
const CLEAR_NIGHT = [
  spot('clear-night', 'nebula', { w: 155, at: [50, 30], opacity: 0.48, blend: 'screen' }),
  full('clear-night', 'stars', { opacity: 0.82, blend: 'screen' }),
  spot('clear-night', 'moon', { w: 30, at: [75, 26], opacity: 1, blend: 'screen' })
]

/* 多云：主云团 + 两片远云拉出层次 */
const CLOUDY = [
  spot('cloudy', 'cloud', { w: 62, at: [40, 38], opacity: 0.82 }),
  spot('cloudy', 'dim75', { w: 44, at: [24, 56], opacity: 0.38 }),
  spot('cloudy', 'dim77', { w: 46, at: [76, 60], opacity: 0.34 })
]

/* 阴天：两片厚重的低云压满画面上半部 */
const OVERCAST = [
  spot('overcast', 'dim77', { w: 64, at: [38, 34], opacity: 0.52 }),
  spot('overcast', 'dim75', { w: 48, at: [72, 64], opacity: 0.42 })
]

/* 雨：整屏雨丝 + 右下角一片雨云 */
const RAIN = [
  full('rain', 'drops', { opacity: 0.80 }),
  spot('rain', 'dim77', { w: 44, at: [74, 66], opacity: 0.30 })
]

/* 强降雨：雨丝更密更亮 */
const HEAVY_RAIN = [
  full('rain', 'drops', { opacity: 0.90 }),
  spot('rain', 'dim77', { w: 44, at: [74, 66], opacity: 0.36 })
]

/* 雪：整屏雪幕 + 两片雪云 */
const SNOW = [
  full('snow', 'overlay', { opacity: 0.55 }),
  spot('snow', 'dim75', { w: 44, at: [28, 50], opacity: 0.22 }),
  spot('snow', 'dim77', { w: 42, at: [74, 64], opacity: 0.20 })
]

/* 雨夹雪：雨丝 + 雪幕 + 高光冰晶在同一帧里并存 */
const SLEET = [
  full('sleet', 'drops', { opacity: 0.52 }),
  full('sleet', 'overlay', { opacity: 0.42 }),
  spot('sleet', 'extra96', { w: 46, at: [74, 34], opacity: 0.55, blend: 'screen' })
]

/* 霾：整片灰白云雾 + 右下雾团，压低的能见度感 */
const HAZE = [
  spot('haze', 'cloud', { w: 96, at: [42, 36], opacity: 0.38 }),
  spot('haze', 'fog', { w: 48, at: [72, 68], opacity: 0.30 })
]

/* 浓雾：比霾更厚、更贴近镜头 */
const FOG = [
  spot('fog', 'cloud', { w: 100, at: [42, 38], opacity: 0.46 }),
  spot('fog', 'fog', { w: 52, at: [70, 68], opacity: 0.38 })
]

/* 冰雹：冰晶 + 水珠 + 雨丝 + 云底 */
const HAIL = [
  spot('hail', 'hkl', { w: 70, at: [64, 34], opacity: 0.85, blend: 'screen' }),
  spot('hail', 'water', { w: 58, at: [32, 50], opacity: 0.50, blend: 'screen' }),
  full('hail', 'drops', { opacity: 0.42 }),
  spot('hail', 'dim77', { w: 42, at: [74, 66], opacity: 0.26 })
]

/* 雷阵雨：切图库里没有闪电素材，用「更密的雨丝 + 压顶的雨云」表达雷雨，
 * 不再借用冰晶（冰晶是冰雹主题的主角，用在雷雨上会看成下雪） */
const THUNDER = [
  full('hail', 'drops', { opacity: 0.82 }),
  spot('hail', 'dim77', { w: 46, at: [72, 64], opacity: 0.34 })
]

/* 浮尘 / 扬沙：底部一条沙尘带 */
const DUST = [
  spot('dust', 'sandstrip', { w: 122, at: [50, 80], opacity: 0.72 })
]

/* 沙尘暴：沙尘糊满整屏 */
const SANDSTORM = [
  spot('dust', 'sandstrip', { w: 135, at: [50, 55], opacity: 0.85 })
]

/* ---------- 薄纱：把文字对比度拉回来 ---------- */
// 深色底：顶部轻微压暗。刻意做得很淡，避免盖掉需求里指定的渐变色阶
// （只补足白字对比度，白字本身还有 text-shadow 兜底）。
const VEIL_ON_DARK = 'linear-gradient(180deg, rgba(0,0,0,.14) 0%, rgba(0,0,0,.04) 46%, rgba(0,0,0,0) 100%)'
// 浅色底：顶部提亮，深字才压得住（雪/雾/霾这类浅色渐变）
const VEIL_ON_LIGHT = 'linear-gradient(180deg, rgba(255,255,255,.30) 0%, rgba(255,255,255,.10) 50%, rgba(0,0,0,.06) 100%)'
// 夜空：已经很暗，几乎不需要薄纱
const VEIL_NIGHT = 'linear-gradient(180deg, rgba(0,0,0,.06) 0%, rgba(0,0,0,.01) 50%, rgba(0,0,0,0) 100%)'

/* ---------- 天气主题表 ---------- */
const RAIN_GRADIENT = ['#617EA1', '#445C79', '#2D4159']
const SNOW_GRADIENT = ['#ACC0D8', '#8DA3BC', '#7187A1']
const DUST_GRADIENT = ['#7E919F', '#9C8E81']

const PLANS = {
  sunny: {
    label: '晴天',
    gradient: ['#36598C', '#4080B3', '#4AA7DB'],
    textMode: 'light',
    veil: VEIL_ON_DARK,
    layers: SUNNY
  },
  clearNight: {
    label: '晴天夜晚',
    gradient: ['#182532', '#1E2C36'],
    textMode: 'light',
    veil: VEIL_NIGHT,
    layers: CLEAR_NIGHT
  },
  cloudy: {
    label: '多云',
    gradient: ['#4B7799', '#78A0B7'],
    textMode: 'light',
    veil: VEIL_ON_DARK,
    layers: CLOUDY
  },
  overcast: {
    label: '阴天',
    gradient: ['#5F7B91', '#91A9B7'],
    textMode: 'light',
    veil: VEIL_ON_DARK,
    layers: OVERCAST
  },
  rain: {
    label: '中雨',
    gradient: RAIN_GRADIENT,
    textMode: 'light',
    veil: VEIL_ON_DARK,
    layers: RAIN
  },
  heavyRain: {
    label: '强降雨',
    gradient: RAIN_GRADIENT,
    textMode: 'light',
    veil: VEIL_ON_DARK,
    layers: HEAVY_RAIN
  },
  thunder: {
    label: '雷阵雨',
    gradient: RAIN_GRADIENT,
    textMode: 'light',
    veil: VEIL_ON_DARK,
    layers: THUNDER
  },
  hail: {
    label: '冰雹',
    gradient: RAIN_GRADIENT,
    textMode: 'light',
    veil: VEIL_ON_DARK,
    layers: HAIL
  },
  sleet: {
    label: '雨夹雪',
    gradient: RAIN_GRADIENT,
    textMode: 'light',
    veil: VEIL_ON_DARK,
    layers: SLEET
  },
  snow: {
    label: '中雪',
    gradient: SNOW_GRADIENT,
    textMode: 'dark',
    veil: VEIL_ON_LIGHT,
    layers: SNOW
  },
  fog: {
    label: '浓雾',
    gradient: ['#647991', '#BFC3C8'],
    textMode: 'dark',
    veil: VEIL_ON_LIGHT,
    layers: FOG
  },
  haze: {
    label: '霾',
    gradient: ['#7E919F', '#8D8F90', '#9C8E81'],
    textMode: 'dark',
    veil: VEIL_ON_LIGHT,
    layers: HAZE
  },
  dust: {
    label: '浮尘/扬沙',
    gradient: DUST_GRADIENT,
    textMode: 'dark',
    veil: VEIL_ON_LIGHT,
    layers: DUST
  },
  sandstorm: {
    label: '沙尘暴',
    gradient: DUST_GRADIENT,
    textMode: 'dark',
    veil: VEIL_ON_LIGHT,
    layers: SANDSTORM
  }
}

/* ---------- QWeather condition code → 主题 ---------- */
const CODE_TO_PLAN = {
  100: 'sunny',
  150: 'clearNight',
  101: 'cloudy', 102: 'cloudy', 103: 'cloudy',
  151: 'cloudy', 152: 'cloudy', 153: 'cloudy', 154: 'cloudy',
  104: 'overcast',
  // 雨：300/301/305/306/307/309/313/314/315/350/351/399
  300: 'rain', 301: 'rain', 305: 'rain', 306: 'rain', 307: 'rain', 309: 'rain',
  313: 'rain', 314: 'rain', 315: 'rain', 350: 'rain', 351: 'rain', 399: 'rain',
  // 强降雨
  308: 'heavyRain', 310: 'heavyRain', 311: 'heavyRain', 312: 'heavyRain',
  316: 'heavyRain', 317: 'heavyRain', 318: 'heavyRain',
  // 雪
  400: 'snow', 401: 'snow', 404: 'snow', 405: 'snow', 406: 'snow',
  407: 'snow', 408: 'snow', 456: 'snow', 457: 'snow', 499: 'snow',
  // 雨夹雪
  402: 'sleet', 403: 'sleet', 409: 'sleet', 410: 'sleet',
  // 雷阵雨 / 冰雹
  302: 'thunder', 303: 'thunder', 304: 'hail',
  // 霾
  502: 'haze', 511: 'haze', 512: 'haze', 513: 'haze',
  // 浓雾
  500: 'fog', 501: 'fog', 509: 'fog', 510: 'fog', 514: 'fog', 515: 'fog',
  // 浮尘 / 扬沙
  503: 'dust', 504: 'dust',
  // 沙尘暴
  507: 'sandstorm', 508: 'sandstorm',
  // 月相
  800: 'clearNight', 801: 'clearNight', 802: 'clearNight', 803: 'clearNight',
  804: 'clearNight', 805: 'clearNight', 806: 'clearNight', 807: 'clearNight'
}

const DEFAULT_KEY = 'sunny'

/** 取某个 condition code 对应的天气主题（含渐变 / 明暗 / 元素层）。 */
function planFor(code) {
  const key = CODE_TO_PLAN[String(code)] || DEFAULT_KEY
  return { key, ...PLANS[key] }
}

/** 仅取元素层（历史调用方用）。 */
function lookup(code) {
  return planFor(code).layers
}

export { planFor, lookup, PLANS, DEFAULT_KEY, RAIN_GRADIENT, SNOW_GRADIENT, DUST_GRADIENT }
